import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useState } from 'react'

import {
  ProviderTaskType,
  UpdateProviderTaskTypeRequest,
} from '../../../providers/ProviderTask/ProviderTask.provider'

interface CreateOrUpdateProviderTaskTypeModalProps {
  showCreateTypeModal: boolean
  setShowCreateTypeModal: React.Dispatch<React.SetStateAction<boolean>>
  taskTypes: ProviderTaskType[]
  setTaskTypes: React.Dispatch<React.SetStateAction<ProviderTaskType[]>>
  isAdmin: boolean
  onCreateTaskType: (type: string) => Promise<any>
  onUpdateTaskType: (request: UpdateProviderTaskTypeRequest) => Promise<any>
  loading: boolean
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
}

export const CreateOrUpdateProviderTaskTypeModal = ({
  showCreateTypeModal,
  setShowCreateTypeModal,
  taskTypes,
  setTaskTypes,
  isAdmin,
  onCreateTaskType,
  onUpdateTaskType,
  loading,
  setLoading,
}: CreateOrUpdateProviderTaskTypeModalProps) => {
  const [newTypeName, setNewTypeName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingName, setEditingName] = useState('')
  const [editingError, setEditingError] = useState<string | null>(null)
  const [updatingId, setUpdatingId] = useState<number | null>(null)

  const handleClose = () => {
    setShowCreateTypeModal(false)
    setNewTypeName('')
    setError(null)
    setEditingId(null)
    setEditingName('')
    setEditingError(null)
  }

  const handleCreateTaskType = async () => {
    if (!newTypeName.trim()) return

    try {
      setLoading(true)
      setError(null)

      const created = await onCreateTaskType(newTypeName.trim())

      if (!created?.id) {
        setError('Failed to create task type. It may already exist.')
        return
      }

      setTaskTypes((prev) =>
        [...prev, created].sort(
          (a, b) =>
            new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime()
        )
      )

      setNewTypeName('')
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          'Failed to create task type. It may already exist.'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleStartEditing = (taskType: ProviderTaskType) => {
    if (updatingId !== null) return

    setEditingId(taskType.id)
    setEditingName(taskType.type)
    setEditingError(null)
  }

  const handleCancelEditing = () => {
    setEditingId(null)
    setEditingName('')
    setEditingError(null)
  }

  const handleUpdateTaskType = async (taskType: ProviderTaskType) => {
    const trimmedName = editingName.trim()

    if (!trimmedName) {
      setEditingError('Task type name cannot be empty.')
      return
    }

    if (trimmedName === taskType.type) {
      handleCancelEditing()
      return
    }

    try {
      setUpdatingId(taskType.id)
      setEditingError(null)

      const updated = await onUpdateTaskType({
        providerTaskTypeId: taskType.id,
        newType: trimmedName,
      })

      if (!updated?.id) {
        setEditingError('Failed to update task type. It may already exist.')
        return
      }

      setTaskTypes((prev) =>
        prev
          .map((item) => (item.id === updated.id ? updated : item))
          .sort(
            (a, b) =>
              new Date(b.modifiedAt).getTime() -
              new Date(a.modifiedAt).getTime()
          )
      )

      handleCancelEditing()
    } catch (err: any) {
      setEditingError(
        err?.response?.data?.message ??
          'Failed to update task type. It may already exist.'
      )
    } finally {
      setUpdatingId(null)
    }
  }

  return (
    <Dialog
      open={showCreateTypeModal}
      onClose={handleClose}
      maxWidth='xs'
      fullWidth
    >
      <DialogTitle>Create New Task Type</DialogTitle>

      <DialogContent>
        <TextField
          label='Enter new task type name'
          value={newTypeName}
          onChange={(e) => {
            setNewTypeName(e.target.value)
            if (error) setError(null)
          }}
          size='small'
          fullWidth
          sx={{ mt: 1 }}
          error={!!error}
          disabled={loading}
        />

        {error && (
          <Alert severity='error' sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}

        <Button
          variant='contained'
          onClick={handleCreateTaskType}
          disabled={loading || !newTypeName.trim()}
          sx={{
            mt: 1.5,
            textTransform: 'none',
          }}
        >
          {loading ? <CircularProgress size={20} color='inherit' /> : 'Create'}
        </Button>
      </DialogContent>

      {isAdmin && (
        <Paper
          elevation={0}
          sx={{
            mx: 2,
            my: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 1,
              py: 1.25,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant='subtitle2' fontWeight={600}>
              All Task Types
            </Typography>
          </Box>

          <Box
            sx={{
              height: 280,
              overflowY: 'auto',
              px: 1,
              py: 1,

              '&::-webkit-scrollbar': {
                width: 6,
              },

              '&::-webkit-scrollbar-thumb': {
                borderRadius: 3,
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
              },
            }}
          >
            {taskTypes.map((taskType) => {
              const isEditing = editingId === taskType.id
              const isUpdating = updatingId === taskType.id

              return (
                <Box key={taskType.id}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      minHeight: 40,
                      mb: 0.5,
                      padding: 1,
                      borderRadius: 1,
                      backgroundColor: isEditing
                        ? 'action.selected'
                        : 'transparent',
                      transition: 'background-color 0.15s ease',

                      '&:hover': {
                        backgroundColor: isEditing
                          ? 'action.selected'
                          : 'action.hover',
                      },
                    }}
                  >
                    {isEditing ? (
                      <TextField
                        value={editingName}
                        onChange={(e) => {
                          setEditingName(e.target.value)
                          if (editingError) {
                            setEditingError(null)
                          }
                        }}
                        size='small'
                        fullWidth
                        disabled={isUpdating}
                        error={!!editingError}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleUpdateTaskType(taskType)
                          }

                          if (e.key === 'Escape') {
                            handleCancelEditing()
                          }
                        }}
                        sx={{
                          '& .MuiInputBase-root': {
                            height: 34,
                          },
                          border: 'none',
                        }}
                      />
                    ) : (
                      <Typography
                        variant='body2'
                        fontWeight={500}
                        color='text.primary'
                        sx={{ flex: 1 }}
                      >
                        {taskType.type}
                      </Typography>
                    )}

                    {isEditing ? (
                      <>
                        <Tooltip title='Save'>
                          <span>
                            <IconButton
                              size='small'
                              color='primary'
                              onClick={() => handleUpdateTaskType(taskType)}
                              disabled={isUpdating}
                            >
                              {isUpdating ? (
                                <CircularProgress size={18} />
                              ) : (
                                <CheckOutlinedIcon
                                  fontSize='small'
                                  color='success'
                                />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title='Cancel'>
                          <span>
                            <IconButton
                              size='small'
                              onClick={handleCancelEditing}
                              disabled={isUpdating}
                              sx={{
                                color: 'text.secondary',
                              }}
                            >
                              <CloseOutlinedIcon
                                fontSize='small'
                                color='error'
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    ) : (
                      <Tooltip title='Edit task type'>
                        <span>
                          <IconButton
                            size='small'
                            onClick={() => handleStartEditing(taskType)}
                            disabled={editingId !== null}
                            sx={{
                              color: 'text.secondary',
                              '&:hover': {
                                color: 'primary.main',
                                backgroundColor: 'action.selected',
                              },
                            }}
                          >
                            <EditOutlinedIcon fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>

                  {isEditing && editingError && (
                    <Alert
                      severity='error'
                      sx={{
                        mx: 1.5,
                        mb: 0.75,
                        py: 0,
                        '& .MuiAlert-message': {
                          py: 0.5,
                        },
                      }}
                    >
                      {editingError}
                    </Alert>
                  )}
                </Box>
              )
            })}
          </Box>
        </Paper>
      )}

      <DialogActions>
        <Button onClick={handleClose} disabled={loading || updatingId !== null}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
